/**
 * A custom element that toggles a product/variant in the customer's
 * wishlist. Requires login; degrades to hidden if the wishlist backend
 * is unreachable (e.g. app uninstalled) so it never breaks the page
 * it's rendered in.
 *
 * @extends {HTMLElement}
 */
class WishlistButton extends HTMLElement {
  /** @type {HTMLButtonElement | null} */
  toggleButton = null;

  connectedCallback() {
    this.toggleButton = this.querySelector('.wishlist-button__toggle');
    if (!this.toggleButton) return;

    this.toggleButton.addEventListener('click', this.#handleClick);

    if (this.dataset.customerLoggedIn === 'true') {
      this.#syncState();
      window.wishlistStore?.addEventListener('change', this.#syncState);
    }
  }

  disconnectedCallback() {
    window.wishlistStore?.removeEventListener('change', this.#syncState);
  }

  #syncState = async () => {
    try {
      await window.wishlistStore.load();
      if (!window.wishlistStore.available) {
        this.hidden = true;
        return;
      }
      this.hidden = false;
      const wishlisted = window.wishlistStore.has(this.dataset.variantId);
      this.#setPressed(wishlisted);
    } catch {
      this.hidden = true;
    }
  };

  #setPressed(pressed) {
    if (!this.toggleButton) return;
    this.toggleButton.setAttribute('aria-pressed', String(pressed));
    this.toggleButton.setAttribute(
      'aria-label',
      pressed
        ? this.toggleButton.dataset.removeLabel || this.toggleButton.getAttribute('aria-label')
        : this.toggleButton.dataset.addLabel || this.toggleButton.getAttribute('aria-label')
    );
  }

  #handleClick = async (event) => {
    event.preventDefault();

    if (this.dataset.customerLoggedIn !== 'true') {
      const loginUrl = this.dataset.loginUrl;
      if (loginUrl) {
        const separator = loginUrl.includes('?') ? '&' : '?';
        window.location.href = `${loginUrl}${separator}return_url=${encodeURIComponent(window.location.href)}`;
      }
      return;
    }

    const isPressed = this.toggleButton?.getAttribute('aria-pressed') === 'true';
    const nextPressed = !isPressed;

    // Optimistic update.
    this.#setPressed(nextPressed);

    try {
      const response = await fetch(`/apps/wishlist/${nextPressed ? 'add' : 'remove'}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product_id: this.dataset.productId,
          variant_id: this.dataset.variantId,
        }),
      });

      if (!response.ok) throw new Error('Wishlist request failed');

      if (nextPressed) {
        window.wishlistStore?.add(this.dataset.variantId);
      } else {
        window.wishlistStore?.remove(this.dataset.variantId);
      }

      this.dispatchEvent(
        new CustomEvent('wishlist:change', {
          bubbles: true,
          detail: { productId: this.dataset.productId, variantId: this.dataset.variantId, wishlisted: nextPressed },
        })
      );
    } catch {
      // Revert optimistic update on failure (network error, app unreachable, etc.)
      this.#setPressed(isPressed);
    }
  };
}

if (!customElements.get('wishlist-button')) {
  customElements.define('wishlist-button', WishlistButton);
}
